// File: src/components/common/Toast.jsx
// Fixed-position toast container — mount once in App.jsx.
// Listens for 'drivezoe:toast' custom events dispatched by src/utils/toast.js.

import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

const ICONS = {
  success: <CheckCircle2 size={18} />,
  error:   <XCircle     size={18} />,
  info:    <Info        size={18} />,
};

const COLORS = {
  success: { bg: '#d1fae5', border: '#10b981', text: '#065f46', icon: '#10b981' },
  error:   { bg: '#fee2e2', border: '#ef4444', text: '#991b1b', icon: '#ef4444' },
  info:    { bg: '#dbeafe', border: '#3b82f6', text: '#1e3a8a', icon: '#3b82f6' },
};

const Toast = () => {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = ({ detail }) => {
      const { id, type, message, duration = 4000 } = detail;
      setToasts(prev => [...prev, { id, type, message }]);
      setTimeout(() => remove(id), duration);
    };
    document.addEventListener('drivezoe:toast', handler);
    return () => document.removeEventListener('drivezoe:toast', handler);
  }, [remove]);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position:      'fixed',
      top:           '1.25rem',
      right:         '1.25rem',
      zIndex:        99999,
      display:       'flex',
      flexDirection: 'column',
      gap:           '0.6rem',
      maxWidth:      '22rem',
      width:         '100%',
      pointerEvents: 'none'       // let clicks pass through the gap between toasts
    }}>
      {toasts.map(({ id, type, message }) => {
        const c = COLORS[type] || COLORS.info;
        return (
          <div key={id} style={{
            pointerEvents:  'auto',
            display:        'flex',
            alignItems:     'flex-start',
            gap:            '0.6rem',
            padding:        '0.875rem 1rem',
            background:     c.bg,
            border:         `1.5px solid ${c.border}`,
            borderRadius:   '0.6rem',
            boxShadow:      '0 4px 16px rgba(0,0,0,0.12)',
            animation:      'bipzToastIn 0.2s ease',
            fontFamily:     "'DM Sans','Segoe UI',sans-serif"
          }}>
            {/* Icon */}
            <span style={{ color: c.icon, flexShrink: 0, marginTop: '0.05rem' }}>
              {ICONS[type] || ICONS.info}
            </span>

            {/* Message */}
            <span style={{
              flex:       1,
              fontSize:   '0.875rem',
              color:      c.text,
              lineHeight: 1.5,
              fontWeight: 500
            }}>
              {message}
            </span>

            {/* Dismiss */}
            <button
              onClick={() => remove(id)}
              style={{
                flexShrink:  0,
                background:  'none',
                border:      'none',
                cursor:      'pointer',
                color:       c.icon,
                padding:     0,
                display:     'flex',
                alignItems:  'center',
                opacity:     0.7
              }}
            >
              <X size={15} />
            </button>
          </div>
        );
      })}

      {/* Keyframe injected inline — avoids needing a CSS file */}
      <style>{`
        @keyframes bipzToastIn {
          from { opacity: 0; transform: translateX(1.5rem); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default Toast;